import React, { useEffect } from 'react';
import { Link } from 'react-router-dom';
import { PublicNav } from '../components/PublicNav';
import { Logo } from '../components/Brand';
import { Card } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { MessageGregBubble } from '../components/MessageGregBubble';
import { trackCtaClick } from '../lib/analytics';
import {
  GraduationCap, Radio, Video, Sparkles, ChevronRight, CheckCircle2, ShoppingCart,
  ClipboardList, Users, Ticket, Globe, CreditCard, Film, FileText, Shield, PlayCircle,
  MessageSquare, Lock,
} from 'lucide-react';

/**
 * Services — public landing page dedicated to the online-education and
 * live-video offerings inside K9os. Different from the Features grid on
 * the home page: this page is for the dog-training educator persona
 * (course creators, webinar hosts, 1-on-1 online coaches) and walks
 * through exactly how each product works, what it replaces, and what it
 * costs them (nothing extra — included).
 */
const PRIMARY = '#C8102E';
const INK = '#0B1220';
const INDIGO = '#1B3A87';

const Row = ({ icon: Icon, children }) => (
  <li className='flex items-start gap-2.5 text-sm text-slate-700 dark:text-slate-300'>
    <Icon className='w-4 h-4 mt-0.5 shrink-0 text-[#167A5A]' />
    <span>{children}</span>
  </li>
);

const ServiceCard = ({ accent, eyebrow, title, blurb, bullets, testid }) => (
  <Card
    className='relative overflow-hidden p-6 sm:p-7 bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-800 hover:shadow-lg transition-shadow flex flex-col h-full'
    data-testid={testid}
  >
    <div
      aria-hidden='true'
      className='absolute inset-x-0 top-0 h-1'
      style={{ background: `linear-gradient(90deg, ${PRIMARY}, ${INDIGO})` }}
    />
    <div className='flex items-start justify-between gap-3 mb-3'>
      <div>
        <div className='text-[10px] uppercase tracking-[0.22em] font-bold text-[#C8102E]'>{eyebrow}</div>
        <h3 className='font-display text-xl sm:text-2xl font-bold text-[#0B1220] dark:text-slate-50 leading-tight mt-1'>{title}</h3>
      </div>
      <div
        className='w-11 h-11 rounded-xl flex items-center justify-center text-white shrink-0'
        style={{ background: accent }}
      >
        {/* placeholder slot (icon passed via children-less card not used) */}
      </div>
    </div>
    <p className='text-sm text-slate-600 dark:text-slate-300 leading-relaxed mb-4'>{blurb}</p>
    <ul className='space-y-2 mb-5'>
      {bullets.map((b, i) => <Row key={i} icon={b.icon || CheckCircle2}>{b.text}</Row>)}
    </ul>
    <div className='mt-auto text-[11px] text-slate-500 dark:text-slate-400'>Included in every K9os plan · no per-seat fees.</div>
  </Card>
);

/* A wider "spotlight" card with a stronger icon tile used for the three
   education products (Courses, Live Events, 1-on-1 Video). */
const Spotlight = ({ icon: Icon, accent, eyebrow, title, blurb, bullets, testid }) => (
  <Card
    className='relative overflow-hidden p-6 sm:p-7 bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-800 hover:shadow-lg transition-shadow flex flex-col h-full'
    data-testid={testid}
  >
    <div aria-hidden='true' className='absolute inset-x-0 top-0 h-1' style={{ background: `linear-gradient(90deg, ${accent}, ${INDIGO})` }} />
    <div className='flex items-center gap-3 mb-3'>
      <div
        className='w-12 h-12 rounded-xl flex items-center justify-center text-white shadow-md shrink-0'
        style={{ background: accent }}
      >
        <Icon className='w-6 h-6' />
      </div>
      <div className='min-w-0'>
        <div className='text-[10px] uppercase tracking-[0.22em] font-bold text-[#C8102E]'>{eyebrow}</div>
        <h3 className='font-display text-xl sm:text-2xl font-bold text-[#0B1220] dark:text-slate-50 leading-tight'>{title}</h3>
      </div>
    </div>
    <p className='text-sm sm:text-base text-slate-600 dark:text-slate-300 leading-relaxed mb-4'>{blurb}</p>
    <ul className='space-y-2 mb-5'>
      {bullets.map((b, i) => <Row key={i} icon={b.icon || CheckCircle2}>{b.text}</Row>)}
    </ul>
    <div className='mt-auto text-[11px] text-slate-500 dark:text-slate-400'>Included in every K9os plan · no per-seat fees.</div>
  </Card>
);

export default function Services() {
  // SEO — set title, meta description, keywords, OpenGraph, Twitter card,
  // canonical, and inject JSON-LD so Google/Bing/GPT index this page for
  // "online dog training LMS" and sibling long-tail queries. All DOM
  // mutations are done on mount and cleaned up on unmount so the next
  // route reverts to its own meta.
  useEffect(() => {
    const prevTitle = document.title;
    const TITLE = 'Online Dog Training LMS, Live Webinars & 1-on-1 Peer Video | K9os App';
    const DESC = 'K9os is the all-in-one online dog training LMS: build drip-fed courses with fillable worksheets, sell tickets to live-streamed webinars, and run 1-on-1 peer video consults. No per-student fees, no third-party keys. Book a free demo.';
    const KEYWORDS = 'online dog training LMS, dog training course platform, dog training webinar software, live dog training stream, 1-on-1 dog training video, dog trainer online course software, sell dog training courses online, dog training e-learning platform, online dog training classes software, dog training live event ticketing, dog training membership site, Teachable alternative dog trainers, Thinkific alternative dog trainers, Kajabi alternative dog trainers, dog training webinar platform, K9os LMS';
    const URL = 'https://k9os.app/services';
    const OG_IMAGE = 'https://k9os.app/img/k9os-og-v4.png';

    document.title = TITLE;

    const setMeta = (keyAttr, keyValue, content) => {
      let el = document.querySelector(`meta[${keyAttr}="${keyValue}"]`);
      if (!el) {
        el = document.createElement('meta');
        el.setAttribute(keyAttr, keyValue);
        document.head.appendChild(el);
      }
      el.setAttribute('content', content);
      return el;
    };

    const metaEls = [
      setMeta('name', 'description', DESC),
      setMeta('name', 'keywords', KEYWORDS),
      setMeta('name', 'robots', 'index,follow,max-image-preview:large,max-snippet:-1'),
      // OpenGraph
      setMeta('property', 'og:title', TITLE),
      setMeta('property', 'og:description', DESC),
      setMeta('property', 'og:url', URL),
      setMeta('property', 'og:type', 'website'),
      setMeta('property', 'og:site_name', 'K9os App'),
      setMeta('property', 'og:image', OG_IMAGE),
      setMeta('property', 'og:image:alt', 'K9os App — Online Dog Training LMS, Live Webinars, and 1-on-1 Peer Video'),
      // Twitter
      setMeta('name', 'twitter:card', 'summary_large_image'),
      setMeta('name', 'twitter:title', TITLE),
      setMeta('name', 'twitter:description', DESC),
      setMeta('name', 'twitter:image', OG_IMAGE),
    ];

    // Canonical
    let canonical = document.querySelector('link[rel="canonical"]');
    const hadCanonical = !!canonical;
    const prevCanonicalHref = canonical?.getAttribute('href') || '';
    if (!canonical) {
      canonical = document.createElement('link');
      canonical.setAttribute('rel', 'canonical');
      document.head.appendChild(canonical);
    }
    canonical.setAttribute('href', URL);

    // JSON-LD — triple schema stack: SoftwareApplication (the product),
    // ItemList of three OnlineCourse / EducationEvent / Service nodes
    // that spell out each offering, and a short FAQPage for the long-tail
    // "is there an online dog training LMS?" style queries.
    const graph = {
      '@context': 'https://schema.org',
      '@graph': [
        {
          '@type': 'SoftwareApplication',
          '@id': `${URL}#software`,
          name: 'K9os Online Education Suite',
          operatingSystem: 'Web',
          applicationCategory: 'BusinessApplication',
          applicationSubCategory: 'LearningManagementSystem',
          description: DESC,
          url: URL,
          provider: { '@type': 'Organization', name: 'K9os App', url: 'https://k9os.app' },
          offers: { '@type': 'Offer', price: '125', priceCurrency: 'USD', url: 'https://k9os.app/pricing' },
          featureList: [
            'Online dog training LMS with lesson videos',
            'Fillable and printable worksheets with auto-save',
            'Per-student course progress tracking',
            'Ticketed live-streamed dog training webinars',
            'Shoppable live-stream merch sidebar (POS upsells)',
            'Access-code gated live streaming',
            '1-on-1 peer video consults with private rooms',
            'Small-group video coaching',
            'Native Stripe and PayPal payouts',
          ],
        },
        {
          '@type': 'Course',
          '@id': `${URL}#course-product`,
          name: 'K9os Online Courses — LMS for dog trainers',
          description: 'Build drip-fed curricula: upload lesson videos, attach PDFs, and design fillable worksheets or printable homework your students complete from their own portal.',
          provider: { '@type': 'Organization', name: 'K9os App', url: 'https://k9os.app' },
          hasCourseInstance: {
            '@type': 'CourseInstance',
            courseMode: 'Online',
            courseWorkload: 'Self-paced',
          },
        },
        {
          '@type': 'Service',
          '@id': `${URL}#live-events`,
          serviceType: 'Live-streamed dog training webinars and ticketed events',
          name: 'K9os Live Events & Webinars',
          description: 'Sell tickets to paid live-streamed masterclasses, cap attendance, and pin POS products to a shoppable merch sidebar.',
          provider: { '@type': 'Organization', name: 'K9os App', url: 'https://k9os.app' },
          areaServed: 'Worldwide',
        },
        {
          '@type': 'Service',
          '@id': `${URL}#peer-video`,
          serviceType: '1-on-1 and small-group online dog training video coaching',
          name: 'K9os 1-on-1 Peer Video',
          description: 'Private video rooms for online dog training consults — no downloads, no accounts, just a secure link.',
          provider: { '@type': 'Organization', name: 'K9os App', url: 'https://k9os.app' },
          areaServed: 'Worldwide',
        },
        {
          '@type': 'FAQPage',
          '@id': `${URL}#faq`,
          mainEntity: [
            {
              '@type': 'Question',
              name: 'Is K9os an online dog training LMS?',
              acceptedAnswer: {
                '@type': 'Answer',
                text: 'Yes. K9os includes a full online dog training LMS where trainers build courses with lesson videos, PDFs, and fillable or printable worksheets, then enroll clients who complete the course from their own portal with saved progress.',
              },
            },
            {
              '@type': 'Question',
              name: 'Can I sell tickets to live dog training webinars with K9os?',
              acceptedAnswer: {
                '@type': 'Answer',
                text: 'Yes. K9os Live Events support paid or free tickets with PayPal checkout, capped attendance, access-code gated streams, and an in-stream merch sidebar you can pin up to 6 POS products to.',
              },
            },
            {
              '@type': 'Question',
              name: 'Does K9os support 1-on-1 online dog training video sessions?',
              acceptedAnswer: {
                '@type': 'Answer',
                text: 'Yes. K9os generates a private video room per call for 1-on-1 or small-group peer coaching sessions. Clients see the link in their own dashboard — no third-party accounts or downloads required.',
              },
            },
            {
              '@type': 'Question',
              name: 'Are there per-student fees to sell dog training courses on K9os?',
              acceptedAnswer: {
                '@type': 'Answer',
                text: 'No. The K9os online education suite is included in every plan with no per-student fees. Your Stripe and PayPal accounts stay yours — K9os never sits between you and your students\u2019 payments.',
              },
            },
          ],
        },
      ],
    };

    const jsonld = document.createElement('script');
    jsonld.type = 'application/ld+json';
    jsonld.id = 'services-jsonld';
    jsonld.text = JSON.stringify(graph);
    document.head.appendChild(jsonld);

    return () => {
      document.title = prevTitle;
      document.getElementById('services-jsonld')?.remove();
      // Leave meta tags in place but reset to generic site defaults next route owns.
      // Canonical: restore previous value if it existed, else remove ours.
      if (hadCanonical) canonical?.setAttribute('href', prevCanonicalHref);
      else canonical?.remove();
      // Note: we intentionally do not strip the meta tags we created — the
      // next page we navigate to runs its own `setMeta` and overwrites
      // them, so cleanup is idempotent and safe.
      void metaEls;
    };
  }, []);

  return (
    <div className='min-h-screen flex flex-col bg-white dark:bg-[#0B1220]' data-testid='page-services'>
      <PublicNav />

      {/* Hero */}
      <section
        className='relative overflow-hidden text-white'
        style={{ background: 'radial-gradient(1200px 500px at 20% 0%, #172554 0%, #0B1220 55%, #050810 100%)' }}
      >
        <div className='absolute inset-0 opacity-25' aria-hidden='true'
          style={{ backgroundImage: 'radial-gradient(rgba(147,197,253,0.14) 1px, transparent 1px)', backgroundSize: '24px 24px' }} />
        <div className='relative max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-16 sm:py-24 text-center'>
          <Badge className='bg-[#C8102E] text-white mb-4'><Sparkles className='w-3 h-3 mr-1' /> Online education suite</Badge>
          <h1 className='font-display text-3xl sm:text-5xl md:text-6xl font-bold tracking-tight leading-tight' data-testid='services-hero-title'>
            Teach dog training online — <span className='text-[#F59E0B]'>without the SaaS stack.</span>
          </h1>
          <p className='mt-4 text-slate-200 text-base sm:text-lg max-w-3xl mx-auto leading-relaxed'>
            K9os now bundles a full online-education studio for working-dog professionals:
            an on-demand <strong className='text-white'>LMS</strong>, ticketed{' '}
            <strong className='text-white'>live-stream events</strong>, and private{' '}
            <strong className='text-white'>1-on-1 peer video calls</strong> — all under
            the same login you already use for bookings, POS, and CRM.
          </p>
          <div className='mt-7 flex flex-col sm:flex-row items-center justify-center gap-3'>
            <Link
              to='/pricing'
              onClick={() => trackCtaClick('pricing', 'services_hero')}
              className='inline-flex items-center justify-center gap-2 px-6 py-3 rounded-full bg-[#C8102E] hover:bg-[#A50D26] text-white font-bold text-sm shadow-lg hover:-translate-y-0.5 transition-all'
              data-testid='services-hero-pricing-cta'
            >
              See pricing & packages <ChevronRight className='w-4 h-4' />
            </Link>
            <Link
              to='/contact'
              onClick={() => trackCtaClick('book_a_demo', 'services_hero')}
              className='inline-flex items-center justify-center gap-2 px-6 py-3 rounded-full bg-white/10 border border-white/20 text-white hover:bg-white/20 font-bold text-sm transition-all backdrop-blur-sm'
              data-testid='services-hero-demo-cta'
            >
              Book a free demo
            </Link>
          </div>
          <div className='mt-7 flex items-center justify-center gap-5 text-xs text-slate-300 flex-wrap'>
            <span className='inline-flex items-center gap-1'><CheckCircle2 className='w-3.5 h-3.5 text-emerald-400' /> No extra per-student fees</span>
            <span className='inline-flex items-center gap-1'><CheckCircle2 className='w-3.5 h-3.5 text-emerald-400' /> No third-party video keys</span>
            <span className='inline-flex items-center gap-1'><CheckCircle2 className='w-3.5 h-3.5 text-emerald-400' /> Keeps 100% of ticket revenue</span>
          </div>
        </div>
      </section>

      {/* Three-product grid */}
      <section className='py-14 sm:py-20 bg-white dark:bg-[#0B1220] border-b border-slate-200 dark:border-slate-800'>
        <div className='max-w-6xl mx-auto px-4 sm:px-6 lg:px-8'>
          <div className='max-w-3xl mx-auto text-center mb-10'>
            <div className='text-[10px] uppercase tracking-[0.22em] font-bold text-[#C8102E] mb-2'>What you get</div>
            <h2 className='font-display text-3xl sm:text-4xl font-bold text-[#0B1220] dark:text-slate-50'>
              Three ways to monetise your knowledge.
            </h2>
            <p className='mt-3 text-slate-600 dark:text-slate-300'>
              Pre-record once. Teach live. Or sit 1-on-1 with a client across the country.
              All three sit inside the K9os dashboard you already know.
            </p>
          </div>

          <div className='grid md:grid-cols-3 gap-5'>
            <Spotlight
              icon={GraduationCap}
              accent={INDIGO}
              eyebrow='LMS · On-demand'
              title='Online Courses'
              blurb='Build drip-fed curricula: upload lesson videos, attach PDFs, and design fillable worksheets or printable homework your students complete from their own portal.'
              bullets={[
                { icon: Film, text: 'Unlimited lessons with video + notes + attachments' },
                { icon: ClipboardList, text: 'Fillable worksheets with auto-save drafts' },
                { icon: FileText, text: 'Printable PDF worksheets, uploaded answers' },
                { icon: Users, text: 'Hand-pick enrolled students — no public catalog needed' },
                { icon: CheckCircle2, text: 'Per-student progress tracking & completion %' },
              ]}
              testid='services-card-courses'
            />
            <Spotlight
              icon={Radio}
              accent={PRIMARY}
              eyebrow='Live streaming · Ticketed'
              title='Live Events & Webinars'
              blurb='Run paid masterclasses, Q&A sessions, or launch days as live-streamed events. Cap attendance, sell tickets at your price, and pin POS products to a shoppable merch sidebar.'
              bullets={[
                { icon: Ticket, text: 'Ticketed with PayPal checkout, free events supported' },
                { icon: ShoppingCart, text: 'Pin up to 6 POS products to the live merch sidebar' },
                { icon: Lock, text: 'Access-code gated stream — no password sharing' },
                { icon: Users, text: '~75 attendees per room, free of extra bills' },
                { icon: Globe, text: 'Public ticket page for socials, Google, and SEO' },
              ]}
              testid='services-card-live-events'
            />
            <Spotlight
              icon={Video}
              accent='#167A5A'
              eyebrow='Peer coaching · Private'
              title='1-on-1 Peer Video Calls'
              blurb='Book private online consults or run small-group sessions with your clients. A new video room is spun up per call, with the slug as the auth gate — no accounts, no downloads.'
              bullets={[
                { icon: Video, text: 'HD 1-on-1 and small-group video calls' },
                { icon: PlayCircle, text: 'Host joins as moderator automatically' },
                { icon: MessageSquare, text: 'Room link shows in each client’s own dashboard' },
                { icon: Shield, text: 'Unguessable room slugs — no public listings' },
                { icon: CreditCard, text: 'Charge for consults on the same Stripe/PayPal rail' },
              ]}
              testid='services-card-video-calls'
            />
          </div>
        </div>
      </section>

      {/* "What it replaces" strip */}
      <section className='py-14 sm:py-20 bg-slate-50 dark:bg-slate-950 border-b border-slate-200 dark:border-slate-800'>
        <div className='max-w-6xl mx-auto px-4 sm:px-6 lg:px-8'>
          <div className='max-w-3xl mx-auto text-center mb-10'>
            <div className='text-[10px] uppercase tracking-[0.22em] font-bold text-[#C8102E] mb-2'>Why bundle it</div>
            <h2 className='font-display text-3xl sm:text-4xl font-bold text-[#0B1220] dark:text-slate-50'>
              Cancel the à la carte stack.
            </h2>
            <p className='mt-3 text-slate-600 dark:text-slate-300'>
              What online dog trainers pay for separately — K9os bundles into one login.
              Here&rsquo;s the honest replacement list.
            </p>
          </div>
          <div className='grid sm:grid-cols-2 lg:grid-cols-3 gap-4'>
            {[
              { from: 'Teachable / Kajabi / Thinkific', to: 'K9os Online Courses', note: 'Lessons, worksheets, and progress — without the $149/mo + per-student tax.' },
              { from: 'Zoom Webinars + Eventbrite', to: 'K9os Live Events', note: 'One link covers ticket sales, cap, access codes, and the stream.' },
              { from: 'Daily.co / Whereby / Google Meet', to: '1-on-1 Peer Video', note: 'Private rooms, zero API keys, zero attendee signups.' },
              { from: 'Gumroad / Stan Store', to: 'POS + Merch Sidebar', note: 'Attach your own products to the stream, keep your Stripe/PayPal.' },
              { from: 'Mailchimp / Flodesk broadcasts', to: 'Built-in Broadcasts', note: 'Push updates to enrolled students without another CRM.' },
              { from: 'Dropbox / Google Drive course folders', to: 'Media Timeline', note: 'Videos, PDFs, and dog progress reports live on the client’s profile.' },
            ].map((r, i) => (
              <Card key={i} className='p-5 bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-800' data-testid={`services-replace-${i}`}>
                <div className='text-[10px] uppercase tracking-[0.16em] font-bold text-slate-500 dark:text-slate-400'>Replaces</div>
                <div className='font-semibold text-slate-700 dark:text-slate-200 line-through decoration-[#C8102E]/60 mt-1'>{r.from}</div>
                <div className='text-[10px] uppercase tracking-[0.16em] font-bold text-[#167A5A] mt-3'>With K9os</div>
                <div className='font-display text-lg font-bold text-[#0B1220] dark:text-slate-50 leading-tight'>{r.to}</div>
                <p className='text-xs text-slate-500 dark:text-slate-400 mt-2 leading-relaxed'>{r.note}</p>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* Who it's for */}
      <section className='py-14 sm:py-20 bg-white dark:bg-[#0B1220] border-b border-slate-200 dark:border-slate-800'>
        <div className='max-w-6xl mx-auto px-4 sm:px-6 lg:px-8'>
          <div className='max-w-3xl mx-auto text-center mb-10'>
            <div className='text-[10px] uppercase tracking-[0.22em] font-bold text-[#C8102E] mb-2'>Built for</div>
            <h2 className='font-display text-3xl sm:text-4xl font-bold text-[#0B1220] dark:text-slate-50'>
              Dog trainers who also teach online.
            </h2>
          </div>
          <div className='grid md:grid-cols-3 gap-4'>
            {[
              { title: 'Working-line course creators', body: 'Protection, detection, competition OB — package your curriculum and license it worldwide.' },
              { title: 'Puppy-program coaches', body: 'Sell self-paced "foundations" courses alongside your in-person board & train business.' },
              { title: 'Reactivity & behaviour pros', body: 'Run paid live Q&A nights, sell 1-on-1 consults, and keep every session on your platform.' },
            ].map((w, i) => (
              <Card key={i} className='p-5 bg-slate-50 dark:bg-slate-900 border border-slate-200 dark:border-slate-800' data-testid={`services-persona-${i}`}>
                <div className='font-display text-lg font-bold text-[#0B1220] dark:text-slate-50'>{w.title}</div>
                <p className='text-sm text-slate-600 dark:text-slate-300 mt-2'>{w.body}</p>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* CTA band */}
      <section className='py-14 sm:py-20 bg-gradient-to-r from-[#0B1220] via-[#172554] to-[#1B3A87] text-white'>
        <div className='max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center'>
          <Badge className='bg-[#C8102E] text-white mb-3'><Sparkles className='w-3 h-3 mr-1' /> Ship your first course this week</Badge>
          <h2 className='font-display text-3xl sm:text-4xl font-bold leading-tight'>
            One login. One invoice. Zero per-student fees.
          </h2>
          <p className='mt-3 text-slate-200 max-w-2xl mx-auto'>
            Every K9os plan includes the full education suite. Your Stripe and PayPal accounts
            stay yours — we never sit between you and your students&rsquo; payments.
          </p>
          <div className='mt-7 flex flex-col sm:flex-row items-center justify-center gap-3'>
            <Link
              to='/pricing'
              onClick={() => trackCtaClick('pricing', 'services_cta_band')}
              className='inline-flex items-center justify-center gap-2 px-6 py-3 rounded-full bg-[#C8102E] hover:bg-[#A50D26] text-white font-bold text-sm shadow-lg hover:-translate-y-0.5 transition-all'
              data-testid='services-cta-pricing'
            >
              See pricing <ChevronRight className='w-4 h-4' />
            </Link>
            <Link
              to='/contact'
              onClick={() => trackCtaClick('book_a_demo', 'services_cta_band')}
              className='inline-flex items-center justify-center gap-2 px-6 py-3 rounded-full bg-white/10 border border-white/20 text-white hover:bg-white/20 font-bold text-sm transition-all backdrop-blur-sm'
              data-testid='services-cta-demo'
            >
              Book a free demo
            </Link>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className='py-10 bg-white dark:bg-slate-950 border-t border-slate-200 dark:border-slate-800 mt-auto'>
        <div className='max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 flex flex-col md:flex-row gap-4 md:gap-6 items-center justify-between text-center md:text-left'>
          <Logo />
          <div className='text-xs text-slate-500 dark:text-slate-400'>
            © {new Date().getFullYear()} K9os.app · All rights reserved.
          </div>
        </div>
      </footer>

      <MessageGregBubble />
    </div>
  );
}
